import { useState } from "react";
import { Check, X, Loader2, FlaskConical } from "lucide-react";
import { Button } from "../../ui/button.js";
import { Input } from "../../ui/input.js";

interface ReadinessCheck {
  id: string;
  ok: boolean;
  detail: string;
}

interface ReadinessReport {
  workspaceId: string;
  ready: boolean;
  checks: ReadinessCheck[];
}

interface SmokeResult {
  workItemId: string;
  runId: string;
  identifier: string;
}

/**
 * Run readiness preflight (YAG-296): one check that names every missing
 * prerequisite before a workspace relies on server Code Runs, plus a
 * production-safe smoke run that exercises the full path (including the
 * draft-PR handoff) against a linked repo.
 */
export function RunReadinessPanel() {
  const [workspaceId, setWorkspaceId] = useState("");
  const [repoId, setRepoId] = useState("");
  const [checking, setChecking] = useState(false);
  const [report, setReport] = useState<ReadinessReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [smoking, setSmoking] = useState(false);
  const [smoke, setSmoke] = useState<SmokeResult | null>(null);
  const [smokeError, setSmokeError] = useState<string | null>(null);

  async function checkReadiness() {
    if (!workspaceId.trim()) return;
    setChecking(true);
    setError(null);
    setReport(null);
    setSmoke(null);
    try {
      const params = new URLSearchParams({ workspaceId: workspaceId.trim() });
      params.set("target", "publish");
      if (repoId.trim()) params.set("repoId", repoId.trim());
      const res = await fetch(`/api/admin/run-readiness?${params.toString()}`);
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setError(body?.error ?? `Readiness check failed (${res.status})`);
        return;
      }
      const body = (await res.json()) as { report: ReadinessReport };
      setReport(body.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Readiness check failed");
    } finally {
      setChecking(false);
    }
  }

  async function startSmoke() {
    if (!workspaceId.trim() || !repoId.trim()) {
      setSmokeError("Workspace id and repo id are both required for the smoke run.");
      return;
    }
    setSmoking(true);
    setSmokeError(null);
    try {
      const res = await fetch("/api/admin/run-readiness/smoke", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ workspaceId: workspaceId.trim(), repoId: repoId.trim() }),
      });
      const body = (await res.json().catch(() => null)) as
        | (SmokeResult & { error?: string })
        | null;
      if (!res.ok || !body || body.error) {
        setSmokeError(body?.error ?? `Smoke run failed to start (${res.status})`);
        return;
      }
      setSmoke(body);
    } catch (err) {
      setSmokeError(err instanceof Error ? err.message : "Smoke run failed to start");
    } finally {
      setSmoking(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-lg text-[var(--text-primary)]">Run readiness</h2>
        <p className="text-sm text-[var(--text-secondary)]">
          Verifies the complete publication path: effective Work Mission mode,
          credits, the linked and allowlisted repo, least-privilege GitHub auth,
          E2B and candidate-storage config, the public callback URL, and Temporal.
          The smoke run pushes one harmless commit through the full path and
          opens a closable draft PR as the receipt.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Workspace id"
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
          className="w-72"
        />
        <Input
          placeholder="Repo id (optional for check, required for smoke)"
          value={repoId}
          onChange={(e) => setRepoId(e.target.value)}
          className="w-72"
        />
        <Button onClick={() => void checkReadiness()} disabled={checking || !workspaceId.trim()}>
          {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Check readiness
        </Button>
      </div>

      {error ? <p className="text-sm text-[var(--error)]">{error}</p> : null}

      {report ? (
        <div className="rounded-[var(--radius-card)] border border-[var(--print-border)] bg-[var(--print-bg)] shadow-[var(--shadow-print)]">
          <div className="flex items-center justify-between border-b border-[var(--border-default)] px-4 py-2">
            <span className="text-sm font-medium text-[var(--text-primary)]">
              {report.ready ? "Ready — a Code Run can start" : "Not ready"}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void startSmoke()}
              disabled={!report.ready || smoking || !repoId.trim()}
            >
              {smoking ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FlaskConical className="h-4 w-4" />
              )}
              Start smoke run
            </Button>
          </div>
          <ul className="divide-y divide-[var(--border-default)]">
            {report.checks.map((check) => (
              <li key={check.id} className="flex items-start gap-2 px-4 py-2">
                {check.ok ? (
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--success)]" />
                ) : (
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-[var(--error)]" />
                )}
                <div className="min-w-0">
                  <div className="font-mono text-xs text-[var(--text-secondary)]">{check.id}</div>
                  <div className="text-sm text-[var(--text-primary)]">{check.detail}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {smokeError ? <p className="text-sm text-[var(--error)]">{smokeError}</p> : null}
      {smoke ? (
        <p className="text-sm text-[var(--text-primary)]">
          Smoke run started: <span className="font-mono">{smoke.identifier}</span> —{" "}
          <a className="underline" href={`/work?workItem=${encodeURIComponent(smoke.workItemId)}`}>
            open the ticket
          </a>{" "}
          to watch it land a draft PR.
        </p>
      ) : null}
    </div>
  );
}
